<script lang="ts">
	import CaseStudyCard from "@components/CaseStudyCard.svelte";
	import ContentBlocks from "@components/ContentBlocks.svelte";
	import ProductCard from "@components/ProductCard.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Media from "@components/common/Media.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import envelope from "@core/assets/icons/envelope.svg?raw";
	import mobile from "@core/assets/icons/phone.svg?raw";
	import web from "@core/assets/icons/web.svg?raw";
	import { prettifyUrl } from "@core/utils/prettifyUrl.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import type { PartnersGoldDetailProps } from "./PartnersGoldDetailProps.js";

	const { partner, caseStudies, products }: PartnersGoldDetailProps = $props();

	const { tenant } = getGlobalTemplateProps();

	const stickyInfo: { href: string; icon: string; label: string }[] = [
		partner.website && {
			icon: web,
			href: partner.website[tenant.locale],
			label: prettifyUrl(partner.website[tenant.locale]),
		},
		partner.email && {
			icon: envelope,
			href: `mailto:${partner.email[tenant.locale]}`,
			label: partner.email[tenant.locale],
		},
		partner.phone && {
			icon: mobile,
			href: `tel:${partner.phone[tenant.locale]}`,
			label: partner.phone[tenant.locale],
		},
	].filter((info) => info !== undefined);
</script>

<Layout>
	<section class="pb-8 md:pb-16 lg:pb-20 px-2 md:px-8 full-width grid-container mx-auto flex flex-col">
		<div class="flex flex-col gap-4 mb-10 max-w-[calc(72rem+var(--container-gap))] mx-auto">
			<div class="flex flex-col gap-4 max-w-screen-md mx-auto">
				<h1 class="text-[8rem] text-cmcsecondary tracking-[0.02em] font-druk-cond uppercase">
					<Formatted text={partner.name} />
				</h1>
				{#if partner.perex}
					<p class="text-[1.25rem]/[1.5] md:text-xl/[1.5] font-semibold max-w-screen-md">
						<Formatted text={partner.perex[tenant.locale]} />
					</p>
				{/if}
			</div>
		</div>
		<ContentBlocks blocks={partner.content[tenant.locale]} lightboxIdPerBlock align="center" />
	</section>
	{#if partner.showCaseStudiesOnDetail && caseStudies.length > 0}
		<section class="2xl:full-width 2xl:w-[1600px] xl:mx-auto py-8 md:py-16 lg:py-20 flex flex-col gap-1">
			<h2
				class="text-2xl md:text-5xl/[0.9] xl:text-6xl 2xl:text-7xl text-cmcsecondary tracking-[0.02em] font-druk-cond uppercase"
			>
				<Formatted text="Případové studie" />
			</h2>
			<div class="grid grid-cols-2 lg:grid-cols-3 gap-1 md:gap-2 items-stretch">
				{#each caseStudies as caseStudy (caseStudy.id)}
					<CaseStudyCard {caseStudy} class="max-w-[33rem] w-full" />
				{/each}
			</div>
		</section>
	{/if}
	{#if partner.showProductsOnDetail && products.length > 0}
		<section class="2xl:full-width 2xl:w-[1600px] xl:mx-auto py-8 md:py-16 lg:py-20 flex flex-col gap-1">
			<h2
				class="text-2xl md:text-5xl/[0.9] xl:text-6xl 2xl:text-7xl text-cmcsecondary tracking-[0.02em] font-druk-cond uppercase"
			>
				<Formatted text="Produkty" />
			</h2>
			<div class="grid grid-cols-2 lg:grid-cols-3 gap-1 md:gap-2 items-stretch">
				{#each products as product (product.id)}
					<ProductCard {product} class="min-h-[19.125rem] md:min-h-[24.75rem]" />
				{/each}
			</div>
		</section>
	{/if}
	{#snippet stickyBottom()}
		<div class="bg-white/50 p-4 gap-4 rounded-3xl flex justify-self-center w-fit mb-8 mx-auto backdrop-blur-lg">
			<div class="bg-white max-w-24 xs:max-w-full p-4 rounded-2xl xl:w-[17rem] grid justify-center items-center">
				<Media
					class="object-contain w-auto max-h-8 xs:max-h-10 sm:max-h-12 md:max-h-14 lg:max-h-16"
					source={partner.logo}
					alt="Logo {partner.name}"
					height={60}
					width={237}
					sourcesets={{
						"<2xl": [60, 0],
						">2xl": [60, 0],
					}}
				/>
			</div>
			<div class="grid gap-4" style:grid-template-columns="repeat({stickyInfo.length}, minmax(0, 1fr))">
				{#each stickyInfo as info (info)}
					<a
						class="bg-white p-4 rounded-2xl xl:min-w-[17rem] flex-col flex gap-2 justify-center"
						href={info.href}
						target="_blank"
						aria-label={info.label}
					>
						<Icon icon={info.icon} class="size-6 md:size-7 lg:size-8  text-cmcprimary" />
						<div class="font-semibold text-base/tight hidden lg:block">
							{info.label}
						</div>
					</a>
				{/each}
			</div>
		</div>
	{/snippet}
</Layout>
